import imageCompression from 'browser-image-compression';

interface OptimizeOptions {
  width?: number;
  height?: number;
  quality?: number;
}

/**
 * Returns the image URL as-is (Supabase Image Transformation requires Pro plan).
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  _options: OptimizeOptions = {}
): string {
  if (!url) return "/placeholder.svg";
  return url;
}

/**
 * Compresses an image file client-side before upload.
 * Resizes max width/height to 1200px, converts to WebP, and applies 0.8 quality.
 */
export async function compressImage(file: File): Promise<File> {
  // Only compress images, ignore SVGs, PDFs, etc.
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return file;
  }

  const options = {
    maxSizeMB: 1, // Target max size
    maxWidthOrHeight: 1200, // Max dimension
    useWebWorker: true,
    fileType: 'image/webp', // Convert everything to webp
    initialQuality: 0.8,
  };

  try {
    const compressedBlob = await imageCompression(file, options);
    // Convert Blob back to File with .webp extension
    const newFileName = file.name.replace(/\.[^/.]+$/, ".webp");
    return new File([compressedBlob], newFileName, {
      type: "image/webp",
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error("Error compressing image:", error);
    return file; // Fallback to original if compression fails
  }
}

/**
 * Returns undefined since we can't generate responsive srcset
 * without server-side image transformation.
 */
export function generateSrcSet(
  _url: string | null | undefined,
  _widths: number[] = []
): string | undefined {
  return undefined;
}
